import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../../auth/AuthContext';
import { cortexApi, isApiConfigured } from '../../services/cortexApi';
import { AuditTab } from './admin/AuditTab';
import { EntitiesTab } from './admin/EntitiesTab';
import { FacilitiesTab } from './admin/FacilitiesTab';
import { RateCardTab } from './admin/RateCardTab';
import { ReferralsTab } from './admin/ReferralsTab';
import { SectorsTab } from './admin/SectorsTab';
import { UsersTab } from './admin/UsersTab';

// ─── sidebar item ─────────────────────────────────────────────────────────────

const SidebarItem: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}> = ({ active, onClick, label, icon }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left ${active ? 'bg-hr-pink text-white shadow-md' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
  >
    <div className="shrink-0">{icon}</div>
    <span className="text-xs font-bold whitespace-nowrap">{label}</span>
  </button>
);

// ─── main component ───────────────────────────────────────────────────────────

type AdminTab =
  | 'dashboard'
  | 'companies'
  | 'users'
  | 'facilities'
  | 'referrals'
  | 'sectors'
  | 'rates'
  | 'audit';

export const AdminModule: React.FC = () => {
  const { principal } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');

  const statsQuery = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => cortexApi.stats.get(),
    enabled: isApiConfigured(),
    staleTime: 30_000,
  });

  return (
    <div className="flex min-h-screen bg-slate-100 font-sans">
      {/* SIDEBAR */}
      <aside className="w-64 bg-hr-indigo text-slate-300 flex flex-col fixed h-full z-10">
        <div className="p-6 border-b border-slate-800">
          <h2 className="text-white font-bold text-lg tracking-tight">Command Center</h2>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 bg-hr-lime rounded-full animate-pulse"></div>
            <span className="text-micro font-mono text-hr-lime">SYSTEM ONLINE</span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <SidebarItem
            active={activeTab === 'dashboard'}
            onClick={() => setActiveTab('dashboard')}
            label="Overview"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'companies'}
            onClick={() => setActiveTab('companies')}
            label="Company Management"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'users'}
            onClick={() => setActiveTab('users')}
            label="User Management"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'facilities'}
            onClick={() => setActiveTab('facilities')}
            label="Facility Agreements"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'referrals'}
            onClick={() => setActiveTab('referrals')}
            label="Referrals"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'sectors'}
            onClick={() => setActiveTab('sectors')}
            label="Sectors & Industries"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'rates'}
            onClick={() => setActiveTab('rates')}
            label="Rate Engine"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
          />
          <SidebarItem
            active={activeTab === 'audit'}
            onClick={() => setActiveTab('audit')}
            label="Audit Logs"
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="text-micro text-slate-500 uppercase font-bold mb-2">Logged in as</div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-hr-pink flex items-center justify-center font-bold text-white text-micro">
              {principal?.user.displayName.charAt(0) ?? '?'}
            </div>
            <div>
              <div className="text-xs font-bold text-white leading-none">
                {principal?.user.displayName ?? '—'}
              </div>
              <div className="text-micro text-slate-400 mt-0.5">{principal?.legacyRole ?? '—'}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="ml-64 flex-1 p-8 overflow-y-auto">
        {/* --- DASHBOARD --- */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <h1 className="text-xl font-bold text-slate-800">System Overview</h1>
            <div className="grid grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <div className="text-micro font-bold text-slate-400 uppercase">
                  Registered Entities
                </div>
                <div className="text-2xl font-bold text-slate-800 mt-2">
                  {statsQuery.data?.entities ?? '—'}
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <div className="text-micro font-bold text-slate-400 uppercase">Active Users</div>
                <div className="text-2xl font-bold text-slate-800 mt-2">
                  {statsQuery.data?.activeUsers ?? '—'}
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <div className="text-micro font-bold text-slate-400 uppercase">Open Referrals</div>
                <div className="text-2xl font-bold text-slate-800 mt-2">
                  {statsQuery.data?.openReferrals ?? '—'}
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                <div className="text-micro font-bold text-slate-400 uppercase">Events (24 h)</div>
                <div className="text-2xl font-bold text-slate-800 mt-2">
                  {statsQuery.data?.auditEventsLast24h ?? '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- COMPANIES (master/detail, PR #5a.1) --- */}
        {activeTab === 'companies' && (
          <div className="h-[calc(100vh-8rem)]">
            <EntitiesTab />
          </div>
        )}

        {/* --- USERS (master/detail, PR #5a.3) --- */}
        {activeTab === 'users' && (
          <div className="h-[calc(100vh-8rem)]">
            <UsersTab />
          </div>
        )}

        {/* --- FACILITIES (master/detail, PR #5b) --- */}
        {activeTab === 'facilities' && (
          <div className="h-[calc(100vh-8rem)]">
            <FacilitiesTab />
          </div>
        )}

        {/* --- REFERRALS (PR #5d) --- */}
        {activeTab === 'referrals' && (
          <div className="h-[calc(100vh-8rem)]">
            <ReferralsTab />
          </div>
        )}

        {/* --- SECTORS (PR #5g) --- */}
        {activeTab === 'sectors' && <SectorsTab />}

        {/* --- RATE ENGINE (PR #5g — read-only) --- */}
        {activeTab === 'rates' && <RateCardTab />}

        {/* --- AUDIT LOGS (PR #5f) --- */}
        {activeTab === 'audit' && <AuditTab />}
      </main>
    </div>
  );
};
